// =============================================================================
// File: frontend/src/components/PWAInstallPrompt.jsx
// First-Login PWA Installation Prompt
// =============================================================================
//
// Shows on first login to encourage users to install PWA for:
// - Push notifications (especially iOS requires PWA)
// - Offline access
// - Better performance
// - Home screen icon
//
// USAGE in App.jsx:
//   {showPWAPrompt && <PWAInstallPrompt onClose={() => setShowPWAPrompt(false)} ... />}
// =============================================================================

import React, { useState, useEffect } from 'react';

const PWAInstallPrompt = ({ 
  onClose, 
  onInstalled, 
  fetchWithAuth, 
  API_BASE 
}) => {
  const [platform, setPlatform] = useState('unknown');
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Detect platform
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) {
      setPlatform('ios');
    } else if (/Android/.test(ua)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Listen for beforeinstallprompt (Chrome/Android)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Chrome/Android: use native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        markInstalled();
      }
      setDeferredPrompt(null);
    } else {
      // iOS/Safari: show manual instructions
      setShowInstructions(true);
    }
  };

  const markInstalled = async () => {
    try {
      await fetchWithAuth(`${API_BASE}/api/push/pwa-installed`, {
        method: 'POST'
      });
    } catch (e) {
      console.warn('Could not mark PWA installed:', e);
    }
    if (onInstalled) onInstalled();
    onClose();
  };

  const handleDismiss = async () => {
    try {
      await fetchWithAuth(`${API_BASE}/api/push/pwa-prompted`, {
        method: 'POST'
      });
    } catch (e) {
      console.warn('Could not mark PWA prompted:', e);
    }
    onClose();
  };

  // Don't show on desktop
  if (platform === 'desktop') {
    handleDismiss();
    return null;
  }

  return (
    <div className="pwa-prompt-overlay">
      <div className="pwa-prompt-modal">
        {/* Header */}
        <div className="pwa-prompt-header">
          <img 
            src="/icon-192x192.png" 
            alt="Healist" 
            className="pwa-prompt-icon"
          />
          <h2 className="pwa-prompt-title">Install Healist</h2>
          <p className="pwa-prompt-subtitle">Add to your home screen for the best experience</p>
        </div>

        {!showInstructions ? (
          <>
            {/* Benefits */}
            <div className="pwa-prompt-benefits">
              <div className="pwa-prompt-benefit-item">
                <span className="pwa-prompt-benefit-icon"></span>
                <span>Get health reminders & notifications</span>
              </div>
              <div className="pwa-prompt-benefit-item">
                <span className="pwa-prompt-benefit-icon"></span>
                <span>Faster loading & offline access</span>
              </div>
              <div className="pwa-prompt-benefit-item">
                <span className="pwa-prompt-benefit-icon"></span>
                <span>Full-screen app experience</span>
              </div>
              <div className="pwa-prompt-benefit-item">
                <span className="pwa-prompt-benefit-icon"></span>
                <span>One-tap hydration logging</span>
              </div>
            </div>

            {/* Buttons */}
            <div className="pwa-prompt-button-row">
              <button className="pwa-prompt-secondary-button" onClick={handleDismiss}>
                Maybe Later
              </button>
              <button className="pwa-prompt-primary-button" onClick={handleInstallClick}>
                {deferredPrompt ? 'Install Now' : 'Show Me How'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* iOS Instructions */}
            {platform === 'ios' && (
              <div className="pwa-prompt-instructions">
                <div className="pwa-prompt-instructions-title">
                  <span></span> Install on iPhone/iPad
                </div>
                <div className="pwa-prompt-step">
                  <span className="pwa-prompt-step-number">1</span>
                  <span>
                    Tap the <span className="pwa-prompt-highlight">Share</span> button 
                    <span className="pwa-prompt-share-icon">⎙</span> at the bottom of Safari
                  </span>
                </div>
                <div className="pwa-prompt-step">
                  <span className="pwa-prompt-step-number">2</span>
                  <span>
                    Scroll down and tap <span className="pwa-prompt-highlight">Add to Home Screen</span>
                  </span>
                </div>
                <div className="pwa-prompt-step">
                  <span className="pwa-prompt-step-number">3</span>
                  <span>
                    Tap <span className="pwa-prompt-highlight">Add</span> in the top right corner
                  </span>
                </div>
                <div className="pwa-prompt-step">
                  <span className="pwa-prompt-step-number">4</span>
                  <span>
                    Open Healist from your home screen to enable notifications!
                  </span>
                </div>
              </div>
            )}

            {/* Android Instructions (fallback if no prompt) */}
            {platform === 'android' && (
              <div className="pwa-prompt-instructions">
                <div className="pwa-prompt-instructions-title">
                  <span></span> Install on Android
                </div>
                <div className="pwa-prompt-step">
                  <span className="pwa-prompt-step-number">1</span>
                  <span>
                    Tap the <span className="pwa-prompt-highlight">⋮ Menu</span> button in Chrome
                  </span>
                </div>
                <div className="pwa-prompt-step">
                  <span className="pwa-prompt-step-number">2</span>
                  <span>
                    Tap <span className="pwa-prompt-highlight">Add to Home screen</span> or <span className="pwa-prompt-highlight">Install app</span>
                  </span>
                </div>
                <div className="pwa-prompt-step">
                  <span className="pwa-prompt-step-number">3</span>
                  <span>
                    Tap <span className="pwa-prompt-highlight">Install</span> to confirm
                  </span>
                </div>
              </div>
            )}

            <button className="pwa-prompt-done-button" onClick={markInstalled}>
              ✓ I've Installed It
            </button>
            
            <button className="pwa-prompt-skip-button" onClick={handleDismiss}>
              Skip for Now
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;